import React from 'react';
import { useTranslation } from 'react-i18next';
import { ConverterType, CurrencyType, ActualServer, CategorizationConfig } from '../types';
import CurrencyDropdown from './CurrencyDropdown';
import '../App.css';

interface ConverterConfigurationProps {
  activeConverter: ConverterType;
  startDate: string;
  onStartDateChange: (date: string) => void;
  targetCurrency: CurrencyType;
  onTargetCurrencyChange: (currency: CurrencyType) => void;
  sourceCurrency: CurrencyType;
  onSourceCurrencyChange: (currency: CurrencyType) => void;
  ramiTargetCurrency: CurrencyType;
  onRamiTargetCurrencyChange: (currency: CurrencyType) => void;
  budgetCurrency?: string | null;
  enableCategorization: boolean;
  onCategorizationChange: (enabled: boolean) => void;
  categorizationConfig: CategorizationConfig | null;
  servers: ActualServer[];
  loading?: boolean;
  error?: string | null;
  user?: any;
  onShowAuthModal?: (mode?: 'signin' | 'signup') => void;
  hasActiveSubscription?: boolean;
  onShowSubscriptionModal?: () => void;
  isPaywallEnabled?: boolean;
  onShowBetaModal?: (featureName: string, onContinue?: () => void) => void;
}

const ConverterConfiguration: React.FC<ConverterConfigurationProps> = ({
  activeConverter,
  startDate,
  onStartDateChange,
  targetCurrency,
  onTargetCurrencyChange,
  sourceCurrency,
  onSourceCurrencyChange,
  ramiTargetCurrency,
  onRamiTargetCurrencyChange,
  budgetCurrency,
  enableCategorization,
  onCategorizationChange,
  categorizationConfig,
  servers,
  loading = false,
  error = null,
  user,
  onShowAuthModal,
  hasActiveSubscription = false,
  onShowSubscriptionModal,
  isPaywallEnabled = true,
  onShowBetaModal,
}) => {
  const { t } = useTranslation();
  
  // Check if this is the Rami converter (category converter)
  const isRamiConverter = activeConverter.includes('rami') || activeConverter.includes('category');

  const currentTargetCurrency = isRamiConverter ? ramiTargetCurrency : targetCurrency;
  const handleTargetCurrencyChange = isRamiConverter ? onRamiTargetCurrencyChange : onTargetCurrencyChange;
  const hasCurrencyMismatch =
    Boolean(budgetCurrency) && currentTargetCurrency !== budgetCurrency;

  return (
    <div className="currency-selection">
      <h3>{t('title', { ns: 'configuration' })}</h3>
      <div className="currency-config">
        {/* Source Currency - for category converter */}
        {isRamiConverter && (
          <CurrencyDropdown
            value={sourceCurrency}
            onChange={onSourceCurrencyChange}
            label={t('sourceCurrency', { ns: 'configuration' })}
            id="source-currency"
          />
        )}

        <CurrencyDropdown
          value={currentTargetCurrency}
          onChange={handleTargetCurrencyChange}
          label={t('convertTo', { ns: 'configuration' })}
          id="target-currency"
        />

        {budgetCurrency && (
          <p className="budget-currency-info">
            {t('budgetCurrency', { ns: 'configuration', currency: budgetCurrency })}
          </p>
        )}

        {hasCurrencyMismatch && (
          <p className="currency-mismatch-warning" role="alert">
            {t('currencyMismatchWarning', {
              ns: 'configuration',
              convertCurrency: currentTargetCurrency,
              budgetCurrency,
            })}
          </p>
        )}

        {/* Start Date - universal label with conditional required indicator */}
        <div className="date-group">
          <label htmlFor="start-date">
            {t('startDate', { ns: 'configuration' })}
            {activeConverter.includes('rami') && <span className="required-indicator"> *</span>}
          </label>
          <input
            type="date"
            id="start-date"
            value={startDate}
            onChange={(e) => onStartDateChange(e.target.value)}
            className="date-input"
            required={activeConverter.includes('rami')}
          />
        </div>

        {/* Categorization toggle */}
        {!user ? (
          <div className="subscribe-cta" onClick={() => onShowAuthModal?.('signup')}>
            <div className="subscribe-content">
              {isPaywallEnabled ? (
                <>
                  <h4>{t('subscribeNow', { ns: 'configuration' })}</h4>
                  <p>{t('subscribeDescription', { ns: 'configuration' })}</p>
                </>
              ) : (
                <>
                  <h4>{t('joinBeta', { ns: 'configuration' })}</h4>
                  <p>{t('betaDescription', { ns: 'configuration' })}</p>
                </>
              )}
              <button className="btn btn-primary">
                {t('getStarted', { ns: 'configuration' })}
              </button>
            </div>
          </div>
        ) : (
          <div className="categorization-group">
            <label className="categorization-label">
              <input
                type="checkbox"
                checked={enableCategorization}
                onChange={(e) => {
                  // Check subscription when enabling categorization with Actual Budget servers
                  if (e.target.checked && servers.length > 0) {
                    if (isPaywallEnabled && !hasActiveSubscription) {
                      onShowSubscriptionModal?.();
                      return;
                    } else if (!isPaywallEnabled) {
                      onShowBetaModal?.(t('categorization', { ns: 'features' }), () => {
                        onCategorizationChange(true);
                      });
                      return;
                    }
                  }
                  onCategorizationChange(e.target.checked);
                }}
                disabled={!activeConverter || servers.length === 0}
                className="categorization-checkbox"
              />
              <span className="categorization-text">
                {t('enableCategorization', { ns: 'configuration' })}
              </span>
            </label>
            
            {enableCategorization && categorizationConfig && (
              <div className="categorization-info">
                <span className="server-info">
                  📊 {t('server', { ns: 'configuration' })} {categorizationConfig.server?.name || 'Unknown Server'}
                </span>
                <span className="budget-info">
                  💰 {t('budget', { ns: 'configuration' })} {categorizationConfig.server?.defaultBudgetName || categorizationConfig.budgetId || 'Unknown Budget'}
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {loading && (
        <div className="loading-message">
          {t('loading', { ns: 'common' })}
        </div>
      )}

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}
    </div>
  );
};

export default ConverterConfiguration; 